/**
 * Bilingual textarea — EN / DE tabs in a single Radix Tabs strip.
 *
 * Used wherever a description field has translation requirements:
 *   - Wizard Step 1 (property description)
 *   - PropertyEditSheet (description)
 *   - RoomEditSheet (description)
 *
 * State is fully controlled. Parent owns `valueEn` / `valueDe` and
 * receives both back via `onChange` whenever either tab edits.
 *
 * Visual hints:
 *   - Char count per tab
 *   - "DE not set" muted warning when EN has content but DE is empty
 *     (and vice versa). Editors can ignore — translations are optional.
 *   - Tab triggers show a small dot when that language has unsaved-but-
 *     non-empty content
 */
import { useId } from 'react'
import { AlertCircle } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import type { BilingualValue } from '@/lib/v2-translations'

// Re-exports for legacy import paths. New code should import directly
// from '@/lib/v2-translations' to keep this file a pure components module.
export type { BilingualValue } from '@/lib/v2-translations'

type Props = {
  valueEn: string
  valueDe: string
  onChange: (next: BilingualValue) => void
  rows?: number
  placeholder?: string
  /** Maximum character count (informational only — not enforced). */
  maxLength?: number
  /** Hide the cross-language "missing translation" callout. */
  hideMissingWarning?: boolean
}

export function BilingualTextarea({
  valueEn,
  valueDe,
  onChange,
  rows = 4,
  placeholder,
  maxLength,
  hideMissingWarning,
}: Props) {
  const baseId = useId()
  const enHasContent = valueEn.trim().length > 0
  const deHasContent = valueDe.trim().length > 0
  const showMissingWarning =
    !hideMissingWarning && enHasContent !== deHasContent && (enHasContent || deHasContent)

  return (
    <div className="space-y-2">
      <Tabs defaultValue="en" className="w-full">
        <div className="flex items-center justify-between gap-2">
          <TabsList className="grid w-fit grid-cols-2">
            <TabsTrigger value="en" className="gap-1.5">
              <span>EN</span>
              {enHasContent && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-primary inline-block"
                  aria-label="English has content"
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="de" className="gap-1.5">
              <span>DE</span>
              {deHasContent && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-primary inline-block"
                  aria-label="German has content"
                />
              )}
            </TabsTrigger>
          </TabsList>
          {showMissingWarning && (
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {enHasContent ? 'DE translation missing' : 'EN translation missing'}
            </p>
          )}
        </div>

        <TabsContent value="en" className="mt-2 space-y-1">
          <Textarea
            id={`${baseId}-en`}
            rows={rows}
            value={valueEn}
            onChange={(e) => onChange({ en: e.target.value, de: valueDe })}
            placeholder={placeholder}
            maxLength={maxLength}
          />
          <CharCounter current={valueEn.length} max={maxLength} />
        </TabsContent>

        <TabsContent value="de" className="mt-2 space-y-1">
          <Textarea
            id={`${baseId}-de`}
            rows={rows}
            value={valueDe}
            onChange={(e) => onChange({ en: valueEn, de: e.target.value })}
            placeholder={placeholder}
            maxLength={maxLength}
          />
          <CharCounter current={valueDe.length} max={maxLength} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function CharCounter({ current, max }: { current: number; max?: number }) {
  if (!max) {
    return <p className="text-xs text-muted-foreground tabular-nums text-right">{current} chars</p>
  }
  return (
    <p
      className={`text-xs tabular-nums text-right ${
        current > max ? 'text-destructive' : 'text-muted-foreground'
      }`}
    >
      {current} / {max}
    </p>
  )
}

